import React, { useState, useEffect, useRef } from 'react';
import { Clock, Plus, Trash2, Copy, Calendar, Globe } from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { EnhancedSchedule, LegacySchedule, DisplaySchedule } from '@/types/display';
import { detectTimezone } from '@/utils/timezoneUtils';
import { parseDisplayHours, convertToLegacySchedule } from '@/utils/scheduleParser';
import DisplayStatusBadge from './DisplayStatusBadge';

interface ScheduleEditorProps {
  schedule: DisplaySchedule;
  city?: string | null;
  state?: string | null;
  onChange: (schedule: EnhancedSchedule) => void;
  className?: string;
}

const DAYS = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' }
];

const HOLIDAY_PRESETS = [
  {
    name: 'Christmas Season',
    start: '11-24',
    end: '01-06',
    description: 'Thanksgiving weekend through Epiphany'
  },
  {
    name: 'Halloween Season',
    start: '10-01',
    end: '10-31',
    description: 'October 1st through Halloween'
  },
  {
    name: '4th of July',
    start: '07-01',
    end: '07-07',
    description: 'July 1st through week after'
  },
  {
    name: 'Year Round',
    start: '01-01',
    end: '12-31',
    description: 'Display runs all year'
  }
];

const COMMON_HOURS = [
  { label: '5:00 PM - 10:00 PM', start: '17:00', end: '22:00' },
  { label: '6:00 PM - 9:00 PM', start: '18:00', end: '21:00' },
  { label: '6:00 PM - 10:00 PM', start: '18:00', end: '22:00' },
  { label: '7:00 PM - 10:00 PM', start: '19:00', end: '22:00' },
  { label: 'Dusk - 10:00 PM', start: '17:30', end: '22:00' },
  { label: 'Dusk - 11:00 PM', start: '17:30', end: '23:00' }
];

const ScheduleEditor: React.FC<ScheduleEditorProps> = ({
  schedule,
  city,
  state,
  onChange,
  className
}) => {
  const [editingSchedule, setEditingSchedule] = useState<EnhancedSchedule>(() => {
    const currentYear = new Date().getFullYear();
    const detectedTimezone = detectTimezone(city, state);
    
    // Convert legacy or create new enhanced schedule
    if (schedule && 'timezone' in schedule) {
      return schedule as EnhancedSchedule;
    } else if (schedule && 'start_date' in schedule) {
      // Convert legacy to enhanced
      const legacy = schedule as LegacySchedule;
      const enhanced: EnhancedSchedule = {
        timezone: detectedTimezone,
        regular_hours: {},
        season_dates: {
          start: legacy.start_date,
          end: legacy.end_date
        }
      };
      
      // Initialize all days as null
      DAYS.forEach(day => {
        enhanced.regular_hours[day.key] = null;
      });
      
      // Set hours for scheduled days
      legacy.days.forEach(dayName => {
        const day = DAYS.find(d => 
          d.label.toLowerCase().startsWith(dayName.toLowerCase()) ||
          d.short.toLowerCase() === dayName.toLowerCase() ||
          d.key.toLowerCase().startsWith(dayName.toLowerCase())
        );
        if (day) {
          enhanced.regular_hours[day.key] = {
            start: legacy.hours.start,
            end: legacy.hours.end
          };
        }
      });
      
      return enhanced;
    } else {
      // Create new enhanced schedule
      const enhanced: EnhancedSchedule = {
        timezone: detectedTimezone,
        regular_hours: {},
        season_dates: {
          start: `${currentYear}-11-24`,
          end: `${currentYear + 1}-01-06`
        }
      };
      
      // Initialize all days as null
      DAYS.forEach(day => {
        enhanced.regular_hours[day.key] = null;
      });
      
      return enhanced;
    }
  });

  const [previewMode, setPreviewMode] = useState(false);
  const [customDateOverride, setCustomDateOverride] = useState({ date: '', start: '', end: '', closed: false });
  const isInitialMount = useRef(true);

  // Update parent when schedule changes (but skip initial mount to avoid infinite loop)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    try {
      onChange(editingSchedule);
    } catch (error) {
      console.error('Error in ScheduleEditor onChange:', error);
    }
  }, [editingSchedule, onChange]);

  const updateRegularHours = (dayKey: string, hours: { start: string; end: string } | null) => {
    setEditingSchedule(prev => ({
      ...prev,
      regular_hours: {
        ...prev.regular_hours,
        [dayKey]: hours
      }
    }));
  };

  const copyHoursToAllDays = (sourceDay: string) => {
    const sourceHours = editingSchedule.regular_hours[sourceDay];
    if (!sourceHours) return;
    
    const newRegularHours = { ...editingSchedule.regular_hours };
    DAYS.forEach(day => {
      newRegularHours[day.key] = { ...sourceHours };
    });
    
    setEditingSchedule(prev => ({
      ...prev,
      regular_hours: newRegularHours
    }));
  };

  const copyHoursToWeekdays = (sourceDay: string) => {
    const sourceHours = editingSchedule.regular_hours[sourceDay];
    if (!sourceHours) return;
    
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const newRegularHours = { ...editingSchedule.regular_hours };
    weekdays.forEach(day => {
      newRegularHours[day] = { ...sourceHours };
    });
    
    setEditingSchedule(prev => ({
      ...prev,
      regular_hours: newRegularHours
    }));
  };

  const copyHoursToWeekends = (sourceDay: string) => {
    const sourceHours = editingSchedule.regular_hours[sourceDay];
    if (!sourceHours) return;
    
    const weekends = ['saturday', 'sunday'];
    const newRegularHours = { ...editingSchedule.regular_hours };
    weekends.forEach(day => {
      newRegularHours[day] = { ...sourceHours };
    });
    
    setEditingSchedule(prev => ({
      ...prev,
      regular_hours: newRegularHours
    }));
  };

  const applyCommonHours = (hours: { start: string; end: string }) => {
    const newRegularHours = { ...editingSchedule.regular_hours };
    DAYS.forEach(day => {
      newRegularHours[day.key] = { ...hours };
    });
    
    setEditingSchedule(prev => ({
      ...prev,
      regular_hours: newRegularHours
    }));
  };

  const updateSeasonDates = (field: 'start' | 'end', value: string) => {
    setEditingSchedule(prev => ({
      ...prev,
      season_dates: {
        ...prev.season_dates,
        [field]: value
      }
    }));
  };

  const applySeasonPreset = (preset: typeof HOLIDAY_PRESETS[0]) => {
    const currentYear = new Date().getFullYear();
    const startYear = preset.start.startsWith('01-') ? currentYear + 1 : currentYear;
    const endYear = preset.end.startsWith('01-') ? currentYear + 1 : currentYear;
    
    setEditingSchedule(prev => ({
      ...prev,
      season_dates: {
        start: `${startYear}-${preset.start}`,
        end: `${endYear}-${preset.end}`
      }
    }));
  };

  const addDateOverride = () => {
    if (!customDateOverride.date) return;
    
    const overrideValue = customDateOverride.closed 
      ? null 
      : { start: customDateOverride.start || '18:00', end: customDateOverride.end || '22:00' };
    
    setEditingSchedule(prev => ({
      ...prev,
      date_overrides: {
        ...prev.date_overrides,
        [customDateOverride.date]: overrideValue
      }
    }));
    
    setCustomDateOverride({ date: '', start: '', end: '', closed: false });
  };

  const removeDateOverride = (date: string) => {
    setEditingSchedule(prev => {
      const newOverrides = { ...prev.date_overrides };
      delete newOverrides[date];
      return {
        ...prev,
        date_overrides: newOverrides
      };
    });
  };

  const clearAllHours = () => {
    const newRegularHours = { ...editingSchedule.regular_hours };
    DAYS.forEach(day => {
      newRegularHours[day.key] = null;
    });
    
    setEditingSchedule(prev => ({
      ...prev,
      regular_hours: newRegularHours
    }));
  };

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Schedule Editor
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label htmlFor="preview-mode" className="text-sm">Preview</Label>
              <Switch
                id="preview-mode"
                checked={previewMode}
                onCheckedChange={setPreviewMode}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Preview Mode */}
          {previewMode && (
            <div className="space-y-4">
              <DisplayStatusBadge
                schedule={editingSchedule}
                city={city}
                state={state}
                variant="detailed"
                showRefresh={true}
              />
              <Separator />
            </div>
          )}

          {/* Timezone */}
          <div>
            <Label className="text-sm font-medium flex items-center gap-2 mb-2">
              <Globe className="h-4 w-4" />
              Timezone
            </Label>
            <div className="flex items-center gap-2">
              <Input
                value={editingSchedule.timezone}
                onChange={(e) => setEditingSchedule(prev => ({ ...prev, timezone: e.target.value }))}
                placeholder="America/New_York"
                className="font-mono text-sm"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const detected = detectTimezone(city, state);
                  setEditingSchedule(prev => ({ ...prev, timezone: detected }));
                }}
              >
                Auto-detect
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Auto-detected: {detectTimezone(city, state)}
            </p>
          </div>

          {/* Season Dates */}
          <div>
            <Label className="text-sm font-medium flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4" />
              Season Dates
            </Label>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <Label htmlFor="season-start" className="text-xs text-muted-foreground">Start Date</Label>
                <Input
                  id="season-start"
                  type="date"
                  value={editingSchedule.season_dates?.start || ''}
                  onChange={(e) => updateSeasonDates('start', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="season-end" className="text-xs text-muted-foreground">End Date</Label>
                <Input
                  id="season-end"
                  type="date"
                  value={editingSchedule.season_dates?.end || ''}
                  onChange={(e) => updateSeasonDates('end', e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {HOLIDAY_PRESETS.map((preset) => (
                <Button
                  key={preset.name}
                  variant="outline"
                  size="sm"
                  onClick={() => applySeasonPreset(preset)}
                  className="text-xs"
                >
                  {preset.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Common Hours Quick Apply */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Quick Apply Hours</Label>
            <div className="grid grid-cols-2 gap-2">
              {COMMON_HOURS.map((hours, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => applyCommonHours(hours)}
                  className="text-xs justify-start"
                >
                  {hours.label}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllHours}
              className="text-xs mt-2 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Clear All Hours
            </Button>
          </div>

          {/* Weekly Schedule */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Weekly Schedule</Label>
            <div className="space-y-3">
              {DAYS.map((day) => {
                const hours = editingSchedule.regular_hours[day.key];
                const isActive = hours !== null;
                
                return (
                  <div key={day.key} className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className="w-20 text-sm font-medium">{day.short}</div>
                    
                    <Switch
                      checked={isActive}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          updateRegularHours(day.key, { start: '18:00', end: '22:00' });
                        } else {
                          updateRegularHours(day.key, null);
                        }
                      }}
                    />
                    
                    {isActive && hours && (
                      <>
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            type="time"
                            value={hours.start}
                            onChange={(e) => updateRegularHours(day.key, { ...hours, start: e.target.value })}
                            className="w-32"
                          />
                          <span className="text-muted-foreground">to</span>
                          <Input
                            type="time"
                            value={hours.end}
                            onChange={(e) => updateRegularHours(day.key, { ...hours, end: e.target.value })}
                            className="w-32"
                          />
                        </div>
                        
                        <TooltipProvider>
                          <div className="flex gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyHoursToAllDays(day.key)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Copy to all days</TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyHoursToWeekdays(day.key)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Badge variant="outline" className="text-xs px-1">M-F</Badge>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Copy to weekdays</TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyHoursToWeekends(day.key)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Badge variant="outline" className="text-xs px-1">S-S</Badge>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Copy to weekends</TooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Special Dates */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Special Dates & Overrides</Label>
            
            {/* Add New Override */}
            <div className="grid grid-cols-12 gap-2 items-end mb-4">
              <div className="col-span-3">
                <Label htmlFor="override-date" className="text-xs text-muted-foreground">Date</Label>
                <Input
                  id="override-date"
                  type="date"
                  value={customDateOverride.date}
                  onChange={(e) => setCustomDateOverride(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              
              <div className="col-span-2">
                <Label className="text-xs text-muted-foreground">Closed</Label>
                <div className="flex items-center justify-center h-10">
                  <Switch
                    checked={customDateOverride.closed}
                    onCheckedChange={(checked) => setCustomDateOverride(prev => ({ ...prev, closed: checked }))}
                  />
                </div>
              </div>
              
              {!customDateOverride.closed && (
                <>
                  <div className="col-span-2">
                    <Label htmlFor="override-start" className="text-xs text-muted-foreground">Start</Label>
                    <Input
                      id="override-start"
                      type="time"
                      value={customDateOverride.start}
                      onChange={(e) => setCustomDateOverride(prev => ({ ...prev, start: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="override-end" className="text-xs text-muted-foreground">End</Label>
                    <Input
                      id="override-end"
                      type="time"
                      value={customDateOverride.end}
                      onChange={(e) => setCustomDateOverride(prev => ({ ...prev, end: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              <div className="col-span-3">
                <Button
                  onClick={addDateOverride}
                  disabled={!customDateOverride.date}
                  size="sm"
                  className="w-full"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Override
                </Button>
              </div>
            </div>
            
            {/* Existing Overrides */}
            {editingSchedule.date_overrides && Object.keys(editingSchedule.date_overrides).length > 0 && (
              <div className="space-y-2">
                {Object.entries(editingSchedule.date_overrides)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([date, hours]) => (
                    <div key={date} className="flex items-center justify-between p-2 border rounded">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">
                          {new Date(date).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {hours ? `${hours.start.substring(0, 5)} - ${hours.end.substring(0, 5)}` : 'Closed'}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDateOverride(date)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduleEditor;